/**
 * Utility functions for file handling and processing
 */

/**
 * Sanitize filename to remove spaces and special characters for safe storage
 * Replaces spaces with underscores and removes/replaces unsafe characters
 * This prevents URL encoding issues and ensures consistent file naming
 * 
 * @param {string} filename - The original filename to sanitize
 * @returns {string} - The sanitized filename
 */
export const sanitizeFilename = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return 'unknown_file';
  }

  // Split filename and extension
  const lastDotIndex = filename.lastIndexOf('.');
  const nameWithoutExt = lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
  const extension = lastDotIndex > 0 ? filename.substring(lastDotIndex) : '';

  // Sanitize the name part
  let sanitizedName = nameWithoutExt
    .replace(/\s+/g, '_')           // Replace spaces with underscores
    .replace(/[^a-zA-Z0-9._-]/g, '') // Remove unsafe characters (keep dots, hyphens, underscores)
    .replace(/_+/g, '_')            // Remove multiple consecutive underscores
    .replace(/^_+|_+$/g, '');       // Remove leading/trailing underscores

  // Sanitize the extension part
  let sanitizedExt = extension
    .replace(/[^a-zA-Z0-9.]/g, ''); // Remove unsafe characters from extension

  // Ensure name is not empty after sanitization
  if (!sanitizedName) {
    sanitizedName = 'unknown_file';
  }

  const result = sanitizedName + sanitizedExt;
  
  // Log the sanitization for debugging
  if (result !== filename) {
    console.log(`📁 [FILENAME-SANITIZE] '${filename}' → '${result}'`);
  }

  return result;
};

/**
 * Create a new File object with a sanitized filename
 * 
 * @param {File} originalFile - The original file object
 * @returns {File} - New file object with sanitized filename
 */
export const sanitizeFile = (originalFile) => {
  const sanitizedFilename = sanitizeFilename(originalFile.name);
  
  // Only create a new File object if the name actually changed
  if (sanitizedFilename === originalFile.name) {
    return originalFile;
  }

  // Create new File object with sanitized filename
  const sanitizedFile = new File([originalFile], sanitizedFilename, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });

  return sanitizedFile;
};

/**
 * Get file extension from filename
 * 
 * @param {string} filename - The filename
 * @returns {string} - The file extension (including the dot)
 */
export const getFileExtension = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return '';
  }
  
  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex > 0 ? filename.substring(lastDotIndex) : '';
};

/**
 * Format file size in human readable format
 * 
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Validate filename for potential issues
 * 
 * @param {string} filename - The filename to validate
 * @returns {Object} - Validation result with warnings/suggestions
 */
export const validateFilename = (filename) => {
  const issues = [];
  const suggestions = [];
  
  if (!filename) {
    issues.push('Empty filename');
    return { valid: false, issues, suggestions };
  }
  
  // Check for spaces
  if (/\s/.test(filename)) {
    issues.push('Filename contains spaces');
    suggestions.push('Spaces will be replaced with underscores');
  }
  
  // Check for special characters
  if (/[^a-zA-Z0-9._-]/.test(filename.replace(/\s/g, ''))) {
    issues.push('Filename contains special characters');
    suggestions.push('Special characters will be removed');
  }
  
  // Check for multiple consecutive dots/underscores
  if (/[._-]{2,}/.test(filename)) {
    issues.push('Filename contains consecutive special characters');
    suggestions.push('Consecutive characters will be normalized');
  }
  
  return {
    valid: issues.length === 0,
    issues,
    suggestions,
    sanitized: sanitizeFilename(filename)
  };
};